import type { PayrollApprovalPolicy } from './approval-policy';
import type { PartialSendState } from './partial-send-view';
import type { BackgroundCommand } from './background-command';
import type { PayrollCorrectionReview } from './corrections';
import type { PayrollSummary, PayrollPage } from './bounded-contract';
export type WorkRunState =
  | 'DRAFT'
  | 'BUILDING'
  | 'HELD'
  | 'READY'
  | 'APPROVED'
  | 'HANDOFF_PENDING'
  | 'PROVIDER_ACCEPTED'
  | 'RECONCILED'
  | 'CLOSED'
  | 'CANCELLED'
  | 'SUPERSEDED';
export const WORK_STATE_LABELS: Record<WorkRunState, string> = {
  DRAFT: 'Not built',
  BUILDING: 'Building payroll',
  HELD: 'Held pay needs review',
  READY: 'Ready for approval',
  APPROVED: 'Approved',
  HANDOFF_PENDING: 'Sending to Xero',
  PROVIDER_ACCEPTED: 'Sent to Xero — checking pending',
  RECONCILED: 'Xero matches',
  CLOSED: 'Closed with payment evidence',
  CANCELLED: 'Cancelled',
  SUPERSEDED: 'Replaced by a later build',
};
export interface PayrollWorkHold {
  reasonCode?: string;
  date?: string;
  sourceId?: string;
  sourceType?: string;
  sourceRequestId?: string;
  id: string;
  workerId?: string;
  itemOnly?: boolean;
  href?: string;
  reason: string;
  nextStep: string;
  owner: string;
}
export interface PayrollWorkProviderEvidence {
  status: 'OK' | 'UNAVAILABLE';
  planWorkers?: {
    workerId: string;
    outcome: string;
    preservedCount: number;
    preservedLines: { providerItemId: string; units: number; amountCents: number; rate?: string }[];
    lines: {
      lineKey: string;
      providerItemId: string;
      label: string;
      date: string;
      units: number;
      rate?: string;
      amountCents: number;
    }[];
  }[];
  externallyEntered?: {
    id: string;
    workerId: string;
    label: string;
    amountCents: number;
    reason: string;
  }[];
  planDigest?: string;
  planApproved?: boolean;
  planChanged?: boolean;
  dayALeavePrepared?: boolean;
  canResume?: boolean;
  earningsReviews?: {
    workerId: string;
    payslipId: string;
    earningsHash: string;
    requestId?: string;
  }[];
  holds: PayrollWorkHold[];
  advisories: string[];
  validationMessages: string[];
  checkerRequired: boolean;
  readback: 'NOT_SENT' | 'UNKNOWN' | 'MISMATCH' | 'MATCHED';
}
export interface PayrollWorkView {
  breakPresumptions?: { timesheetId: string; message: string; workerName: string }[];
  approvalPolicy?: PayrollApprovalPolicy;
  corrections?: PayrollCorrectionReview;
  bounded?: { summary: PayrollSummary; workers: PayrollPage; holds: PayrollPage };
  payRunId: string | null;
  backgroundTasks?:
    | {
        status: 'OK';
        items: Omit<BackgroundCommand, 'leaseToken'>[];
        total: number;
        complete: boolean;
      }
    | { status: 'UNAVAILABLE' };
  state: WorkRunState;
  build: { completed: number; total: number; sealed: boolean; capturing?: boolean } | null;
  workers: {
    id: string;
    name: string;
    amountCents: number | null;
    lines?: {
      id: string;
      component: string;
      units: number;
      amountCents: number;
      breakPresumption?: { message: string; timesheetId: string } | null;
    }[];
  }[];
  holds: PayrollWorkHold[];
  provider: PayrollWorkProviderEvidence;
  connectionId: string | null;
  periodId?: string;
  payRunType?: string;
  rosterPeriodId?: string | null;
  payPeriodEnd?: string;
  providerWrite?: 'NONE' | 'STARTED' | 'UNAVAILABLE';
  preparation?: { configured: boolean; observed: boolean } | null;
  partialSend?: PartialSendState;
  connections?: { id: string; label: string }[];
  recovery?: {
    jobId: string;
    revision: number;
    canReauthorise: boolean;
    makerIsCurrentActor: boolean;
  };
  heldReview?: { jobId: string; digest: string; count: number; acknowledged: boolean };
  /** Approved timesheets held out of the next build (capture-exclusions.ts); never paid until re-approved. */
  captureExclusions?: { digest: string; count: number };
  captureExclusionHolds?: PayrollWorkHold[];
}
export function rebuildWorkViewSelected(mode: { mode: string; source: string }): boolean {
  // READ_FAILED / UNKNOWN_VALUE / NO_ROW are OFF: the legacy page keeps working.
  return mode.source === 'ROW' && mode.mode !== 'OFF';
}
export function payrollWorkCanApprove(view: PayrollWorkView): boolean {
  return (
    view.state === 'READY' &&
    view.build?.sealed === true &&
    view.holds.every((h) => h.itemOnly) &&
    (!view.heldReview || view.heldReview.acknowledged)
  );
}
export function payrollWorkCanPush(view: PayrollWorkView): boolean {
  return (
    (view.state === 'APPROVED' ||
      (view.state === 'HANDOFF_PENDING' && view.provider.canResume === true)) &&
    view.provider.planApproved === true &&
    view.provider.status === 'OK' &&
    view.provider.holds.length === 0 &&
    (view.provider.readback === 'NOT_SENT' || view.provider.canResume === true)
  );
}

/** Role and state hints only; controlled RPCs recheck the current actor. */
export function payrollWorkPermissions(
  view: PayrollWorkView,
  role: string | null | undefined,
  mode: string,
) {
  const officer = ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'PAYROLL_OFFICER', 'FINANCE_OFFICER'].includes(
    role ?? '',
  );
  const active = officer && mode === 'ACTIVE';
  return {
    officer,
    launch: active && view.state === 'DRAFT' && !!view.connectionId,
    reauthorise: active && view.recovery?.canReauthorise === true,
    approve:
      active &&
      payrollWorkCanApprove(view) &&
      (view.recovery?.makerIsCurrentActor !== true ||
        view.approvalPolicy?.paySecondPerson === false),
    prepareLeave: active && view.state === 'APPROVED' && view.build?.sealed === true,
    approvePlan:
      active &&
      view.state === 'APPROVED' &&
      view.provider.status === 'OK' &&
      view.provider.holds.length === 0 &&
      !!view.provider.planDigest &&
      !!view.provider.planWorkers?.length &&
      !view.provider.planApproved &&
      (view.recovery?.makerIsCurrentActor !== true ||
        view.approvalPolicy?.planSecondPerson === false),
    push: active && payrollWorkCanPush(view),
    readback: active && view.provider.status === 'OK' && view.provider.readback !== 'NOT_SENT',
    cancel:
      active &&
      view.providerWrite === 'NONE' &&
      view.corrections?.status === 'OK' &&
      view.corrections.canCancel,
    replace: active && view.corrections?.status === 'OK' && view.corrections.canReplace,
    correct: active && view.corrections?.status === 'OK' && view.corrections.canCorrect,
    close:
      active &&
      view.state === 'RECONCILED' &&
      view.provider.status === 'OK' &&
      view.provider.readback === 'MATCHED',
  };
}

export type PayrollWorkAction =
  | 'LAUNCH'
  | 'REAUTHORISE'
  | 'ACK_HELD_ITEMS'
  | 'APPROVE'
  | 'PREPARE_XERO'
  | 'REFRESH_XERO'
  | 'APPROVE_PLAN'
  | 'PUSH'
  | 'READBACK'
  | 'CLOSE'
  | 'CANCEL'
  | 'REFRESH';
export type PayrollWorkCard =
  | 'inputs'
  | 'build'
  | 'approve'
  | 'prepare'
  | 'plan'
  | 'send'
  | 'check'
  | 'close'
  | 'history';
export const PAYROLL_WORK_CARDS: { id: PayrollWorkCard; label: string }[] = [
  { id: 'inputs', label: 'Ready inputs' },
  { id: 'build', label: 'Build and review held pay' },
  { id: 'approve', label: 'Approve pay' },
  { id: 'prepare', label: 'Get Xero ready' },
  { id: 'plan', label: 'Approve the Xero plan' },
  { id: 'send', label: 'Send payslips' },
  { id: 'check', label: 'Check Xero' },
  { id: 'close', label: 'Close' },
];
export function payrollWorkActionLabel(action: PayrollWorkAction, view: PayrollWorkView) {
  switch (action) {
    case 'LAUNCH':
      return 'Build pay';
    case 'REAUTHORISE':
      return 'Resume build';
    case 'ACK_HELD_ITEMS':
      return `Acknowledge ${view.heldReview?.count ?? 0} held items`;
    case 'APPROVE':
      return 'Finalise and approve';
    case 'PREPARE_XERO':
      return 'Get Xero ready';
    case 'REFRESH_XERO':
      return 'Update Xero before sending';
    case 'APPROVE_PLAN':
      return view.provider.planChanged
        ? 'Review and approve updated Xero plan'
        : 'Approve Xero plan';
    case 'PUSH': {
      const count = view.provider.planWorkers?.length ?? view.workers.length;
      return view.state === 'HANDOFF_PENDING'
        ? 'Resume unsent payslips to Xero'
        : `Send ${count} ${count === 1 ? 'payslip' : 'payslips'} to Xero`;
    }
    case 'READBACK':
      return 'Check Xero again';
    case 'CLOSE':
      return 'Close pay run';
    case 'CANCEL':
      return 'Cancel unsent pay run';
    case 'REFRESH':
      return 'Refresh progress';
  }
}
/** Display capabilities only. Every controlled command rechecks its own authority. */
export function payrollWorkJourney(
  view: PayrollWorkView,
  role: string | null | undefined,
  mode: string,
) {
  const permissions = payrollWorkPermissions(view, role, mode);
  const terminal = ['CLOSED', 'CANCELLED', 'SUPERSEDED'].includes(view.state);
  const common = !permissions.officer
    ? 'A payroll officer, finance officer or administrator must record this decision.'
    : mode !== 'ACTIVE'
      ? 'New payroll is not switched on for sending yet. Ask your administrator.'
      : !view.payRunId
        ? 'Create a draft pay run for this period first.'
        : null;
  const payIndependent =
    view.recovery?.makerIsCurrentActor === true && view.approvalPolicy?.paySecondPerson !== false
      ? 'You started this build. A different payroll officer must finalise and approve it.'
      : null;
  const planIndependent =
    view.recovery?.makerIsCurrentActor === true && view.approvalPolicy?.planSecondPerson !== false
      ? 'You started this build. A different payroll officer must approve the Xero plan.'
      : null;
  const processed =
    !!view.build && !view.build.capturing && view.build.completed === view.build.total;
  const acknowledgement =
    processed && !!view.heldReview && !view.heldReview.acknowledged && !view.build?.sealed;
  const prepared = view.preparation?.observed === true && view.provider.dayALeavePrepared === true;
  const candidates: {
    id: PayrollWorkAction;
    card: PayrollWorkCard;
    enabled: boolean;
    reason: string;
  }[] = [
    {
      id: 'LAUNCH',
      card: 'build',
      enabled: permissions.launch,
      reason: !view.connectionId
        ? 'Select a payroll connection first.'
        : 'A build already exists. Review its saved progress.',
    },
    {
      id: 'REAUTHORISE',
      card: 'build',
      enabled: permissions.reauthorise,
      reason: 'Resume is available only for an interrupted build.',
    },
    {
      id: 'ACK_HELD_ITEMS',
      card: 'build',
      enabled: acknowledgement,
      reason: view.heldReview?.acknowledged
        ? 'The exact held list has been acknowledged; processing must finish for this revision.'
        : 'Held review becomes available when all pay inputs have been processed, before the build finishes.',
    },
    {
      id: 'APPROVE',
      card: 'approve',
      enabled: permissions.approve,
      reason:
        payIndependent ?? 'Finish processing and acknowledge held items before final approval.',
    },
    {
      id: 'PREPARE_XERO',
      card: 'prepare',
      enabled: permissions.prepareLeave,
      reason: 'Finalise and approve pay before preparing Xero.',
    },
    {
      id: 'REFRESH_XERO',
      card: 'send',
      enabled: permissions.prepareLeave && prepared,
      reason: 'Get Xero ready first. Updating Xero keeps your saved settings.',
    },
    {
      id: 'APPROVE_PLAN',
      card: 'plan',
      enabled: permissions.approvePlan && prepared,
      reason:
        planIndependent ??
        (view.provider.planApproved
          ? 'The current Xero plan is already approved.'
          : 'Get Xero ready, then resolve the Xero checks and review the plan.'),
    },
    {
      id: 'PUSH',
      card: 'send',
      enabled: permissions.push && prepared,
      reason: view.provider.planApproved
        ? 'Resolve the Xero checks or confirm the outcome before sending.'
        : 'Review and approve the exact current Xero plan first. A changed plan needs fresh approval.',
    },
    {
      id: 'READBACK',
      card: 'check',
      enabled: permissions.readback,
      reason:
        'Xero checking becomes available after sending. An unavailable read never confirms a match.',
    },
    {
      id: 'CLOSE',
      card: 'close',
      enabled: permissions.close,
      reason: 'Match the Xero result and record independent bank payment evidence first.',
    },
    {
      id: 'CANCEL',
      card: 'history',
      enabled: permissions.cancel,
      reason:
        view.providerWrite === 'UNAVAILABLE' || !view.providerWrite
          ? 'We could not confirm whether anything was sent to Xero. Refresh before cancelling.'
          : 'Sending to Xero has started. Use Correct this pay run or ask Statura support to review the send.',
    },
  ];
  const actions = candidates.map((action) => ({
    ...action,
    enabled: !common && !terminal && action.enabled,
    reason: terminal
      ? 'This run is finished. Review its history or open another period.'
      : (common ?? action.reason),
  }));
  let current: PayrollWorkCard;
  let primary: PayrollWorkAction;
  if (terminal) {
    current = 'history';
    primary = 'REFRESH';
  } else if (!view.build) {
    current = 'build';
    primary = 'LAUNCH';
  } else if (!view.build.sealed) {
    current = 'build';
    primary = acknowledgement
      ? 'ACK_HELD_ITEMS'
      : permissions.reauthorise
        ? 'REAUTHORISE'
        : 'REFRESH';
  } else if (['READY', 'HELD', 'BUILDING'].includes(view.state)) {
    current = 'approve';
    primary = 'APPROVE';
  } else if (view.state === 'RECONCILED') {
    current = 'close';
    primary = 'CLOSE';
  } else if (
    view.state === 'PROVIDER_ACCEPTED' ||
    (view.state === 'HANDOFF_PENDING' && !view.provider.canResume)
  ) {
    current = 'check';
    primary = 'READBACK';
  } else if (!prepared) {
    current = 'prepare';
    primary = 'PREPARE_XERO';
  } else if (!view.provider.planApproved) {
    current = 'plan';
    primary = 'APPROVE_PLAN';
  } else {
    current = 'send';
    primary = 'PUSH';
  }
  const selected = actions.find((a) => a.id === primary);
  return {
    actions,
    current,
    primary,
    terminal,
    reason:
      selected && !selected.enabled
        ? selected.reason
        : current === 'build' && primary === 'REFRESH'
          ? 'Statura is processing pay inputs. Refresh to see saved progress.'
          : 'Review the named run and the current action below.',
  };
}
